import React, { useCallback, useEffect, useState } from "react";
import { executeMutation, executeQuery } from "../../../graphql/client";

// Season night console (epic #307 phase 4, captain mode). The one
// screen a captain needs to run a tourney night David isn't at:
// which season night is tonight (or add tonight's event to the
// season if nobody ticked it), approve everyone who RSVP'd yes in
// one tap (games only count once the logger is approved), watch the
// counted / pending / disputed totals, and copy the night's recap +
// the season standings into the chat afterwards. Standings and
// recaps are computed server-side from the compete log — this tab
// never writes scores. Clearing disputes stays on the admin side.

const OCEAN = "var(--ab-ocean, #0e7c9b)";
const SEAFOAM = "var(--ab-seafoam, #e7f4f3)";

const SEASONS_QUERY = `query CaptainSeasons {
  seasons { seasonId name sport startDate endDate status eventIds }
}`;

const DETAIL_QUERY = `query CaptainSeasonNight($seasonId: ID!) {
  seasonStandings(seasonId: $seasonId) {
    nightsTotal nightsPlayed playersCount gamesCounted leaderName shoutout teamShoutout
    standings { rank userSub name boardWins gameWins gameLosses nightsPlayed }
    nights { socialEventId title date rsvpYes approvedPlayers gamesCounted gamesPending gamesDisputed leaderName leaderBoardWins shoutout }
    final { shoutout }
  }
  seasonEventCandidates(seasonId: $seasonId) { socialEventId title date inSeason }
}`;

const BOARD_FIELDS = `
  socialEventId title date canApprove
  players { userSub name approved gamesLogged gameWins gameLosses boardWins }
  games { gameId status }
`;

const BOARD_QUERY = `query CaptainNightBoard($socialEventId: ID!) {
  eventCompete(socialEventId: $socialEventId) { ${BOARD_FIELDS} }
}`;

const APPROVE_ALL = `mutation CaptainApproveAll($socialEventId: ID!) {
  approveAllEventRsvps(socialEventId: $socialEventId) { ${BOARD_FIELDS} }
}`;

const APPROVE_ONE = `mutation CaptainApproveOne($socialEventId: ID!, $userSub: String!, $approved: Boolean!) {
  approveEventRsvp(socialEventId: $socialEventId, userSub: $userSub, approved: $approved) { ${BOARD_FIELDS} }
}`;

const ADD_NIGHT = `mutation CaptainAddSeasonEvent($seasonId: ID!, $socialEventId: ID!) {
  addSeasonEvent(seasonId: $seasonId, socialEventId: $socialEventId) { seasonId eventIds }
}`;

// Today's date as the event ids spell it (YYYY-MM-DD, Jacksonville).
const todayKey = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const dateOf = (x) => (x && x.date) || String((x && x.socialEventId) || "").slice(0, 10);

// Tonight if there is a season night today; else the next one coming
// up; else the most recent one (the recap is still useful tomorrow).
const defaultNight = (nights, today) => {
  if (!nights.length) return null;
  const todays = nights.find((n) => dateOf(n) === today);
  if (todays) return todays.socialEventId;
  const upcoming = nights.filter((n) => dateOf(n) > today);
  if (upcoming.length) return upcoming[0].socialEventId;
  return nights[nights.length - 1].socialEventId;
};

const defaultSeason = (seasons, today) => {
  const live = seasons.filter((s) => s.status === "active" || s.status === "upcoming");
  const inWindow = live.find(
    (s) => s.startDate && s.endDate && s.startDate <= today && today <= s.endDate
  );
  const pick = inWindow || live[0] || seasons[0];
  return pick ? pick.seasonId : null;
};

const card = {
  border: "1px solid #e6e9f0",
  borderRadius: 10,
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};
const muted = { color: "#6a7385", fontSize: 13 };
const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "4px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "6px", fontSize: 14, verticalAlign: "top" };
const num = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const btn = (primary, disabled) => ({
  padding: "8px 14px",
  background: primary ? OCEAN : "#fff",
  color: primary ? "#fff" : "#333",
  border: primary ? "none" : "1px solid #d7dbe4",
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 700,
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.6 : 1,
  marginRight: 8,
  marginBottom: 6,
});
const pill = (active) => ({
  border: active ? `2px solid ${OCEAN}` : "1px solid #d7dbe4",
  background: active ? SEAFOAM : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
  marginRight: 6,
  marginBottom: 6,
});
const chip = (bg, fg) => ({
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  background: bg,
  color: fg,
});
const select = {
  padding: "8px 10px",
  border: "1px solid #d0d6e0",
  borderRadius: 6,
  fontSize: 14,
  maxWidth: "100%",
};

const Stat = ({ label, value, sub }) => (
  <div style={{ ...card, flex: "1 1 120px", marginBottom: 0, padding: "10px 12px" }}>
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: OCEAN }}>{value}</div>
    {sub && <div style={muted}>{sub}</div>}
  </div>
);

const Shout = ({ title, text, onCopy, primary }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
      <strong style={{ fontSize: 14 }}>{title}</strong>
      <button type="button" style={btn(primary)} onClick={() => onCopy(text, title)}>
        Copy
      </button>
    </div>
    <pre
      style={{
        whiteSpace: "pre-wrap",
        fontFamily: "inherit",
        fontSize: 13,
        background: "#f7f8fa",
        border: "1px solid #e6e9f0",
        borderRadius: 8,
        padding: 10,
        margin: 0,
      }}
    >
      {text}
    </pre>
  </div>
);

export default function SeasonNightTab() {
  const today = todayKey();
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [nightId, setNightId] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showOtherNights, setShowOtherNights] = useState(false);

  const loadSeasons = useCallback(async () => {
    const data = await executeQuery(SEASONS_QUERY);
    const list = data.seasons || [];
    setSeasons(list);
    return list;
  }, []);

  const loadDetail = useCallback(async (sid, keepNight) => {
    const data = await executeQuery(DETAIL_QUERY, { seasonId: sid });
    const d = data.seasonStandings;
    setDetail(d);
    setCandidates(data.seasonEventCandidates || []);
    const nights = d ? d.nights : [];
    const stillThere = keepNight && nights.some((n) => n.socialEventId === keepNight);
    setNightId(stillThere ? keepNight : defaultNight(nights, today));
  }, [today]);

  const loadBoard = useCallback(async (sid) => {
    if (!sid) {
      setBoard(null);
      return;
    }
    const data = await executeQuery(BOARD_QUERY, { socialEventId: sid });
    setBoard(data.eventCompete);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await loadSeasons();
        const first = defaultSeason(list, today);
        if (cancelled) return;
        setSeasonId(first);
        if (first) await loadDetail(first, null);
      } catch (err) {
        if (!cancelled) setError(err.message || "Could not load seasons");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSeasons, loadDetail, today]);

  useEffect(() => {
    let cancelled = false;
    loadBoard(nightId).catch((err) => {
      if (!cancelled) setError(err.message || "Could not load the board");
    });
    return () => {
      cancelled = true;
    };
  }, [nightId, loadBoard]);

  const pickSeason = async (sid) => {
    setSeasonId(sid);
    setError("");
    setNotice("");
    setBoard(null);
    try {
      await loadDetail(sid, null);
    } catch (err) {
      setError(err.message || "Could not load that season");
    }
  };

  // Every board mutation hands back the refreshed board; the season
  // table moves too, so refresh the detail quietly afterwards.
  const runBoard = async (mutation, variables, key, doneText) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const data = await executeMutation(mutation, variables);
      setBoard(data[key]);
      if (doneText) setNotice(doneText);
      if (seasonId) await loadDetail(seasonId, nightId);
    } catch (err) {
      setError(err.message || "That didn't save — try again.");
    } finally {
      setBusy(false);
    }
  };

  const approveAll = () => {
    const pending = (board ? board.players : []).filter((p) => !p.approved).length;
    runBoard(
      APPROVE_ALL,
      { socialEventId: nightId },
      "approveAllEventRsvps",
      pending
        ? `Approved ${pending} ${pending === 1 ? "player" : "players"} — their games count now.`
        : "Everyone was already approved."
    );
  };

  const approveOne = (userSub, approved) =>
    runBoard(
      APPROVE_ONE,
      { socialEventId: nightId, userSub, approved },
      "approveEventRsvp",
      approved ? "Approved." : "Approval revoked."
    );

  const addNight = async (sid) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await executeMutation(ADD_NIGHT, { seasonId, socialEventId: sid });
      await loadSeasons();
      await loadDetail(seasonId, sid);
      setNightId(sid);
      setNotice("Added to the season. Approve the players below and the games count.");
    } catch (err) {
      setError(err.message || "Could not add that night");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice(`${what} copied — paste it in the chat.`);
    } catch (err) {
      setError("Copy failed — select the text and copy it by hand.");
    }
  };

  if (loading) return <p style={muted}>Loading seasons…</p>;

  const liveSeasons = seasons.filter((s) => s.status !== "archived");
  if (!liveSeasons.length) {
    return (
      <div style={card}>
        <strong>No season yet.</strong>
        <div style={muted}>
          An admin creates one in Admin → Seasons. Until then, tonight's compete board
          still works: open the event from the Calendar and approve players there.
        </div>
      </div>
    );
  }

  const season = liveSeasons.find((s) => s.seasonId === seasonId) || null;
  const nights = detail ? detail.nights : [];
  const night = nights.find((n) => n.socialEventId === nightId) || null;
  const nightNumber = night ? nights.indexOf(night) + 1 : 0;
  const isTonight = night && dateOf(night) === today;
  const todaysCandidates = candidates.filter((c) => !c.inSeason && dateOf(c) === today);
  const otherCandidates = candidates.filter((c) => !c.inSeason && dateOf(c) !== today);
  const players = board ? board.players : [];
  const pendingPlayers = players.filter((p) => !p.approved);
  const gamesBy = (status) => (board ? board.games.filter((g) => g.status === status).length : 0);
  const ended = season && (season.status === "ended" || season.status === "archived");

  return (
    <div>
      {liveSeasons.length > 1 && (
        <div style={{ marginBottom: 8 }}>
          {liveSeasons.map((s) => (
            <button
              key={s.seasonId}
              type="button"
              style={pill(seasonId === s.seasonId)}
              onClick={() => pickSeason(s.seasonId)}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {error && <div style={{ ...card, borderColor: "#f3c2c2", color: "#b02a2a" }}>{error}</div>}
      {notice && <div style={{ ...card, background: SEAFOAM, borderColor: "#bfe0dc" }}>{notice}</div>}

      {season && (
        <div style={card}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
            <div style={{ flex: "1 1 220px" }}>
              <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>
                {season.name}
                {season.sport ? ` · ${season.sport}` : ""}
                {season.startDate && season.endDate ? ` · ${season.startDate} → ${season.endDate}` : ""}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>
                {night
                  ? `${isTonight ? "Tonight" : dateOf(night)} · night ${nightNumber} of ${nights.length}`
                  : "No night picked"}
              </div>
              {night && (
                <div style={muted}>
                  {night.title || night.socialEventId}
                  {!isTonight && dateOf(night) > today ? " (coming up)" : ""}
                  {!isTonight && dateOf(night) < today ? " (already played)" : ""}
                </div>
              )}
            </div>
            {nights.length > 0 && (
              <label style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>
                Switch night{" "}
                <select
                  style={select}
                  value={nightId || ""}
                  onChange={(e) => setNightId(e.target.value || null)}
                >
                  {nights.map((n, i) => (
                    <option key={n.socialEventId} value={n.socialEventId}>
                      {i + 1}. {dateOf(n)} — {n.title || n.socialEventId}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {!isTonight && todaysCandidates.length > 0 && !ended && (
            <div style={{ marginTop: 12, padding: 10, background: "#fff6de", borderRadius: 8 }}>
              <strong style={{ fontSize: 14 }}>Tonight's event isn't in the season yet.</strong>
              <div style={muted}>Add it and tonight's games count toward the standings.</div>
              {todaysCandidates.map((c) => (
                <div key={c.socialEventId} style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    style={btn(true, busy)}
                    disabled={busy}
                    onClick={() => addNight(c.socialEventId)}
                  >
                    Add “{c.title || c.socialEventId}” to {season.name}
                  </button>
                </div>
              ))}
            </div>
          )}

          {otherCandidates.length > 0 && !ended && (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                onClick={() => setShowOtherNights((v) => !v)}
                style={{ background: "none", border: "none", color: OCEAN, fontSize: 13, cursor: "pointer", padding: 0 }}
              >
                {showOtherNights ? "Hide" : "Show"} {otherCandidates.length} other{" "}
                {otherCandidates.length === 1 ? "event" : "events"} in the window not in the season
              </button>
              {showOtherNights && (
                <div style={{ marginTop: 6 }}>
                  {otherCandidates.map((c) => (
                    <div key={c.socialEventId} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, padding: "4px 0" }}>
                      <span style={{ flex: 1 }}>
                        {dateOf(c)} — {c.title || c.socialEventId}
                      </span>
                      <button
                        type="button"
                        style={{ ...btn(false, busy), marginRight: 0, marginBottom: 0, padding: "4px 10px", fontSize: 12 }}
                        disabled={busy}
                        onClick={() => addNight(c.socialEventId)}
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div style={card}>
        <strong style={{ fontSize: 14 }}>Running the night</strong>
        <ol style={{ margin: "6px 0 0", paddingLeft: 20, fontSize: 14, lineHeight: 1.5 }}>
          <li>As people arrive, tap <strong>Approve everyone</strong> (or approve one by one). Only approved players' games count.</li>
          <li>Remind the group: every player logs their own games on the event's board. Partners from outside the group are fine as text.</li>
          <li>After the last game, copy <strong>Tonight's recap</strong> and the <strong>Season standings</strong> into the chat.</li>
          <li>Something logged wrong? Approved players can dispute a game on the board; an admin clears disputes.</li>
        </ol>
      </div>

      {night && (
        <div style={card}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
            <Stat label="RSVP'd yes" value={players.length} sub="on this night" />
            <Stat
              label="Approved"
              value={players.length - pendingPlayers.length}
              sub={pendingPlayers.length ? `${pendingPlayers.length} waiting` : "everyone"}
            />
            <Stat label="Games" value={gamesBy("counted")} sub="counted" />
            <Stat
              label="Not counting"
              value={gamesBy("pending") + gamesBy("disputed")}
              sub={`${gamesBy("pending")} pending · ${gamesBy("disputed")} disputed`}
            />
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            {board && board.canApprove && (
              <button
                type="button"
                style={btn(true, busy || !pendingPlayers.length)}
                disabled={busy || !pendingPlayers.length}
                onClick={approveAll}
              >
                {pendingPlayers.length
                  ? `Approve everyone (${pendingPlayers.length})`
                  : "Everyone approved"}
              </button>
            )}
            <a
              href={`/compete/${night.socialEventId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...btn(false), textDecoration: "none", display: "inline-block" }}
            >
              Open the board ↗
            </a>
          </div>

          {board && !board.canApprove && (
            <div style={{ ...muted, marginTop: 6 }}>
              The server doesn't see you as a captain on this session — sign out and back in,
              or ask an admin to add you to the Captains group.
            </div>
          )}

          {players.length === 0 ? (
            <div style={{ ...muted, marginTop: 10 }}>Nobody has RSVP'd yes yet.</div>
          ) : (
            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Player</th>
                    <th style={{ ...th, textAlign: "right" }}>Games</th>
                    <th style={{ ...th, textAlign: "right" }}>W-L</th>
                    <th style={{ ...th, textAlign: "right" }}>Wins</th>
                    <th style={th} />
                  </tr>
                </thead>
                <tbody>
                  {players.map((p) => (
                    <tr key={p.userSub} style={{ borderTop: "1px solid #eef1f6" }}>
                      <td style={td}>
                        {p.name || "Member"}{" "}
                        {p.approved ? (
                          <span style={chip("#e4f5e9", "#1f7a3a")}>approved</span>
                        ) : (
                          <span style={chip("#fff6de", "#8a5a00")}>pending</span>
                        )}
                      </td>
                      <td style={num}>{p.gamesLogged}</td>
                      <td style={num}>
                        {p.gameWins}-{p.gameLosses}
                      </td>
                      <td style={{ ...num, fontWeight: 700 }}>{p.boardWins}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {board && board.canApprove && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => approveOne(p.userSub, !p.approved)}
                            style={{
                              padding: "5px 10px",
                              background: p.approved ? "#fff" : "#1f7a3a",
                              color: p.approved ? "#b02a2a" : "#fff",
                              border: p.approved ? "1px solid #e0b4b4" : "none",
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            {p.approved ? "Revoke" : "Approve"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {detail && (
        <div style={card}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
            <Stat label="Season nights" value={`${detail.nightsPlayed}/${detail.nightsTotal}`} sub="played / scheduled" />
            <Stat label="Players" value={detail.playersCount} sub="with counted games" />
            <Stat
              label="Leader"
              value={detail.leaderName || "—"}
              sub={detail.standings[0] ? `${detail.standings[0].boardWins} wins` : "no games yet"}
            />
          </div>

          {night && night.shoutout ? (
            <Shout title="Tonight's recap" text={night.shoutout} onCopy={copy} primary />
          ) : (
            night && (
              <div style={{ ...muted, marginBottom: 12 }}>
                Tonight's recap appears once a counted game is logged on this night.
              </div>
            )
          )}
          {detail.shoutout && <Shout title="Season standings" text={detail.shoutout} onCopy={copy} />}
          {detail.teamShoutout && <Shout title="Team standings" text={detail.teamShoutout} onCopy={copy} />}
          {detail.final && detail.final.shoutout && (
            <Shout title="Season final" text={detail.final.shoutout} onCopy={copy} primary />
          )}

          {detail.standings.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Player</th>
                    <th style={{ ...th, textAlign: "right" }}>Nights</th>
                    <th style={{ ...th, textAlign: "right" }}>W-L</th>
                    <th style={{ ...th, textAlign: "right" }}>Wins</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.standings.slice(0, 10).map((r) => (
                    <tr key={r.userSub} style={{ borderTop: "1px solid #eef1f6" }}>
                      <td style={num}>{r.rank}</td>
                      <td style={td}>{r.name || "Member"}</td>
                      <td style={num}>{r.nightsPlayed}</td>
                      <td style={num}>
                        {r.gameWins}-{r.gameLosses}
                      </td>
                      <td style={{ ...num, fontWeight: 700 }}>{r.boardWins}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {detail.standings.length > 10 && (
                <div style={{ ...muted, marginTop: 6 }}>
                  Top 10 shown — the full table is at /seasons/{seasonId}.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
